import { useLocation } from 'react-router-dom';
import SEO from '@/components/SEO';

/**
 * Centralized fallback SEO for routes whose page components don't render their
 * own <SEO>/Helmet. Rendered ONCE inside the Router. For any path NOT in the
 * map it renders null, so pages that manage their own meta are untouched.
 * Keep titles < ~60 chars and descriptions ~150-160 chars.
 */
const SEO_MAP = {
  // ── Indexable marketing / tool landings ──
  '/map-scan': {
    title: 'Free Local Map Ranking Scan – Google, Apple, Waze & Yelp',
    description: 'See if your business shows up on Google, Apple Maps, Waze and Yelp. Run a free local map ranking scan and find out where you rank in seconds.',
    keywords: 'local map ranking, google maps ranking, local seo scan, map pack, apple maps, yelp ranking',
  },
  '/super-affiliate': {
    title: 'Top-Tier Partner Program – Recurring Commissions | Web-TechServices',
    description: 'Join the Web-TechServices Top-Tier Partner program and earn recurring commissions referring web design, SEO and AI marketing services.',
    keywords: 'affiliate program, partner program, recurring commissions, referral program, web design affiliate',
  },
  '/partners': {
    title: 'Agency Partner Platform – Manage Clients at Scale',
    description: 'Everything you need to manage clients programmatically: white-label web, SEO and AI tools built for agencies and partners.',
    keywords: 'agency platform, white label seo, partner platform, manage clients, reseller program',
  },
  '/partner/install': {
    title: 'Become a Web-TechServices Brand Partner',
    description: 'Get set up as a Web-TechServices brand partner. Install your partner tools, grab your referral link and start earning in minutes.',
    keywords: 'become a partner, brand partner, partner install, referral link, partner tools',
  },
  '/dont-scan': {
    title: 'Request Your Free QR Review Kit | Web-TechServices',
    description: 'Get a free QR review kit that turns happy customers into 5-star Google reviews. Request your kit and start collecting reviews on autopilot.',
    keywords: 'qr review kit, google reviews, review generation, nfc review, get more reviews',
  },
  '/affiliate/leaderboard': {
    title: 'Partner Leaderboard | Web-TechServices',
    description: 'See the top-performing Web-TechServices partners — live rankings by active affiliates, conversions and earnings.',
    keywords: 'partner leaderboard, affiliate leaderboard, top partners',
  },

  // ── Private / utility / app / payment pages → noindex ──
  '/clauctions': { title: 'Secure Payment Portal | Web-TechServices', description: 'Secure client payment portal.', noindex: true },
  '/ppc-portal': { title: 'PPC Client Portal | Web-TechServices', description: 'Secure PPC client portal.', noindex: true },
  '/affiliate/autopost-setup': { title: 'Auto-Poster Setup | Web-TechServices', description: 'Partner auto-poster setup guide.', noindex: true },
  '/affiliate/linkedin/queue': { title: 'LinkedIn Post Queue | Web-TechServices', description: 'Partner LinkedIn post queue.', noindex: true },
  '/affiliate/password-reset': { title: 'Reset Partner Password | Web-TechServices', description: 'Reset your partner account password.', noindex: true },
  '/affiliate/status': { title: 'Partner Application Status | Web-TechServices', description: 'Check your partner application status.', noindex: true },
  '/confirm-email': { title: 'Confirm Your Email | Web-TechServices', description: 'Confirm your email address.', noindex: true },
  '/jobstream/app': { title: 'JobStream Dashboard | Web-TechServices', description: 'JobStream partner dashboard.', noindex: true },
  '/jobstream/embed': { title: 'Booking Widget | Web-TechServices', description: 'JobStream booking widget.', noindex: true },
  '/portfolio/tools': { title: 'Portfolio Owner Tools | Web-TechServices', description: 'Portfolio owner dashboard.', noindex: true },
  '/reupload': { title: 'Re-upload File | Web-TechServices', description: 'Securely re-upload your file.', noindex: true },
};

const RouteSEO = () => {
  const { pathname } = useLocation();
  const key = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const entry = SEO_MAP[key];
  if (!entry) return null;
  return <SEO {...entry} />;
};

export default RouteSEO;
